import { useEffect, useState } from 'react';
import {
  Activity,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Sparkles,
  TriangleAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  getKpiDiagnosticReport,
  listKpiDiagnosticReports,
  runKpiDiagnostics,
  type KpiDiagnosticDetail,
  type KpiDiagnosticSummary,
  type KpiMetrics,
} from '@/services/googleAdsMonthlyService';
import { useAdsViewStore } from '@/stores/useAdsViewStore';
import { KpiDashboardTab } from './KpiDashboardTab';
import {
  DEFAULT_KPI_DATE_PRESET,
  KPI_DATE_PRESETS,
  presetRange,
  presetUnavailableReason,
  type KpiDatePreset,
} from './kpiDatePresets';

interface KpiDiagnosticsTabProps {
  tenantId: string;
}

export function KpiDiagnosticsTab({ tenantId }: KpiDiagnosticsTabProps) {
  const queryClient = useQueryClient();
  // G1 (client-safety): Diagnostics surfaces operator-only known_facts /
  // likely_causes / next_actions — never show it in client view.
  const isClientView = useAdsViewStore((s) => s.viewMode) === 'client';
  const [view, setView] = useState<'dashboard' | 'diagnostics'>('dashboard');

  // Force back to the Dashboard sub-view whenever we flip into client view, so
  // an operator sitting on Diagnostics can't leave it exposed under the lens.
  useEffect(() => {
    if (isClientView && view !== 'dashboard') setView('dashboard');
  }, [isClientView, view]);

  // In client view the effective sub-view is always Dashboard, regardless of
  // any prior state, so diagnostics content is never rendered.
  const effectiveView = isClientView ? 'dashboard' : view;
  // CORE-PF-04: presets fill the two dates; the default (Last 30 days,
  // ending yesterday) is what the backend computed for an empty request,
  // now shown explicitly so the chip and the inputs agree. Any manual
  // edit of a date flips the active chip to Custom.
  // The explicit default equals the backend's today-1 back 29 days except
  // for browser-local vs server-date drift near midnight.
  const [activePreset, setActivePreset] = useState<KpiDatePreset>(DEFAULT_KPI_DATE_PRESET);
  const [periodStart, setPeriodStart] = useState(
    () => presetRange(DEFAULT_KPI_DATE_PRESET)?.start ?? '',
  );
  const [periodEnd, setPeriodEnd] = useState(
    () => presetRange(DEFAULT_KPI_DATE_PRESET)?.end ?? '',
  );
  const applyPreset = (preset: KpiDatePreset) => {
    if (presetUnavailableReason(preset)) return;
    setActivePreset(preset);
    const range = presetRange(preset);
    if (range) {
      setPeriodStart(range.start);
      setPeriodEnd(range.end);
    }
  };
  const editStart = (value: string) => {
    setPeriodStart(value);
    setActivePreset('custom');
  };
  const editEnd = (value: string) => {
    setPeriodEnd(value);
    setActivePreset('custom');
  };
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [latestResult, setLatestResult] = useState<KpiDiagnosticDetail | null>(null);

  const reports = useQuery({
    queryKey: ['google-ads-kpi-diagnostics', tenantId],
    queryFn: () => listKpiDiagnosticReports(tenantId, 12),
    refetchInterval: 60_000,
  });

  const selectedReport = useQuery({
    queryKey: ['google-ads-kpi-diagnostic-report', selectedReportId],
    queryFn: () => getKpiDiagnosticReport(selectedReportId!),
    enabled: Boolean(selectedReportId),
  });

  const runMutation = useMutation({
    mutationFn: () =>
      runKpiDiagnostics({
        tenantId,
        periodStart: periodStart || undefined,
        periodEnd: periodEnd || undefined,
      }),
    onSuccess: (result) => {
      setLatestResult(result);
      setSelectedReportId(result.report_id ?? result.id);
      toast.success(`KPI diagnostics saved for ${formatPeriod(result)}`);
      queryClient.invalidateQueries({
        queryKey: ['google-ads-kpi-diagnostics', tenantId],
      });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Diagnostics failed: ${detail}`);
    },
  });

  // Item 2 (Art): auto-display the latest saved run on load instead of forcing
  // a click. Once the list loads and nothing is selected, select the newest.
  useEffect(() => {
    if (selectedReportId || latestResult) return;
    const list = reports.data;
    if (list && list.length > 0) {
      const newest = [...list].sort((a, b) =>
        (b.created_at ?? '').localeCompare(a.created_at ?? ''),
      )[0];
      if (newest) setSelectedReportId(newest.id);
    }
  }, [reports.data, selectedReportId, latestResult]);

  const activeReport = selectedReport.data ?? latestResult;

  return (
    <div className="space-y-6">
      <Tabs
        value={effectiveView}
        onValueChange={(v) => setView(v as 'dashboard' | 'diagnostics')}
      >
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          {isClientView ? null : (
            <TabsTrigger value="diagnostics">Diagnostics</TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {effectiveView === 'dashboard' ? (
        <KpiDashboardTab tenantId={tenantId} />
      ) : (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-teal" />
                Run KPI diagnostics
              </CardTitle>
          <CardDescription>
            Defaults to the last complete 30 days against the previous comparable 30 days.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            role="group"
            aria-label="Date presets"
            className="flex flex-wrap items-center gap-1.5"
          >
            {KPI_DATE_PRESETS.map((p) => {
              const unavailable = presetUnavailableReason(p.key);
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p.key)}
                  aria-pressed={activePreset === p.key}
                  aria-disabled={unavailable ? true : undefined}
                  disabled={Boolean(unavailable)}
                  title={unavailable ?? undefined}
                  className={
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ' +
                    (activePreset === p.key
                      ? 'border-coral bg-coral text-white'
                      : 'border-border text-muted-foreground hover:text-foreground')
                  }
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <DateField
              label="Current start"
              value={periodStart}
              onChange={editStart}
            />
            <DateField
              label="Current end"
              value={periodEnd}
              onChange={editEnd}
            />
            <Button
              onClick={() => runMutation.mutate()}
              disabled={runMutation.isPending}
              className="bg-teal hover:bg-teal-hover"
            >
              {runMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Run diagnostics
                </>
              )}
            </Button>
          </div>
          {activeReport ? <KpiDiagnosticDetail report={activeReport} /> : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Saved diagnostics</CardTitle>
          <CardDescription>Historical KPI reports saved for trend review.</CardDescription>
        </CardHeader>
        <CardContent>
          {reports.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading diagnostics...
            </div>
          ) : reports.data && reports.data.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Current window</TableHead>
                  <TableHead>Previous window</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.data.map((report) => (
                  <ReportRow
                    key={report.id}
                    report={report}
                    selected={selectedReportId === report.id}
                    onSelect={() => setSelectedReportId(report.id)}
                  />
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
              No saved KPI diagnostics yet.
            </div>
          )}
        </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="space-y-1">
      <span className="block text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <Input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-[170px]"
      />
    </label>
  );
}

function ReportRow({
  report,
  selected,
  onSelect,
}: {
  report: KpiDiagnosticSummary;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <TableRow className={selected ? 'bg-teal-light/20' : undefined}>
      <TableCell>{formatPeriod(report)}</TableCell>
      <TableCell>
        {report.previous_period_start} {'->'} {report.previous_period_end}
      </TableCell>
      <TableCell>{formatDateTime(report.created_at)}</TableCell>
      <TableCell className="text-right">
        <Button variant="ghost" size="sm" onClick={onSelect}>
          View
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function KpiDiagnosticDetail({ report }: { report: KpiDiagnosticDetail }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <Metric label="Spend" value={money(report.current.spend_usd)} />
        <Metric label="Clicks" value={number(report.current.clicks)} />
        <Metric label="Conversions" value={decimal(report.current.conversions)} />
        <Metric label="CPA" value={money(report.current.cost_per_conversion_usd)} />
      </div>
      <KpiComparisonTable current={report.current} previous={report.previous} />
      {report.warnings.length > 0 ? (
        <div className="rounded-md border border-coral/30 bg-coral-light/20 p-3 text-sm">
          <div className="mb-2 flex items-center gap-2 font-medium text-coral-dark">
            <TriangleAlert className="h-4 w-4" />
            Diagnostic warnings
          </div>
          <ul className="list-disc space-y-1 pl-5">
            {report.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-3">
        <InsightList title="What changed" items={report.known_facts} />
        <InsightList title="Likely causes" items={report.likely_causes} />
        <div className="rounded-md border border-border p-3">
          <div className="mb-3 text-base font-semibold">Next actions</div>
          <div className="space-y-3 text-sm">
            {report.next_actions.map((action) => (
              <div key={action.action} className="space-y-1">
                <div className="flex items-center gap-2">
                  <RiskBadge risk={action.risk_level} />
                  {action.requires_approval ? (
                    <Badge variant="outline">approval required</Badge>
                  ) : null}
                </div>
                <div className="font-medium">{action.action}</div>
                <div className="text-muted-foreground">{action.rationale}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* CORE-G-3: retired the "Client-safe summary" framing — the label
          was operator-visible copy telling the operator what a client
          would see, per Chans's client-facing-copy strip. The one-liner
          text still ships; the data field name (`client_safe_summary`)
          is an API contract that stays. */}
      <div className="rounded-md border border-teal/30 bg-teal-light/20 p-3 text-sm">
        <div className="mb-1 flex items-center gap-2 font-medium text-teal-dark">
          <CheckCircle2 className="h-4 w-4" />
          Summary
        </div>
        <p className="text-muted-foreground">{report.client_safe_summary}</p>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-warm-bg p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

function KpiComparisonTable({
  current,
  previous,
}: {
  current: KpiMetrics;
  previous: KpiMetrics;
}) {
  const rows = [
    ['Spend', money(current.spend_usd), money(previous.spend_usd)],
    ['Clicks', number(current.clicks), number(previous.clicks)],
    ['Impressions', number(current.impressions), number(previous.impressions)],
    ['Conversions', decimal(current.conversions), decimal(previous.conversions)],
    ['CPA', money(current.cost_per_conversion_usd), money(previous.cost_per_conversion_usd)],
    ['CTR', percent(current.ctr), percent(previous.ctr)],
    ['CPC', money(current.cpc_usd), money(previous.cpc_usd)],
    ['Conversion rate', percent(current.conversion_rate), percent(previous.conversion_rate)],
    ['Impression share', percent(current.impression_share), percent(previous.impression_share)],
  ];
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>KPI</TableHead>
          <TableHead>Current</TableHead>
          <TableHead>Previous</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(([label, currentValue, previousValue]) => (
          <TableRow key={label}>
            <TableCell className="font-medium">{label}</TableCell>
            <TableCell>{currentValue}</TableCell>
            <TableCell>{previousValue}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function InsightList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="mb-3 text-base font-semibold">{title}</div>
      <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function RiskBadge({ risk }: { risk: 'low' | 'medium' | 'high' }) {
  const className =
    risk === 'high'
      ? 'border-destructive/40 bg-destructive/10 text-destructive'
      : risk === 'medium'
        ? 'border-amber-400/40 bg-amber-100 dark:bg-amber-900/40/40 text-amber-600 dark:text-amber-400'
        : 'border-[hsl(var(--status-success))]/40 bg-[hsl(var(--status-success-bg))]/40 text-[hsl(var(--status-success))]';
  return (
    <Badge variant="outline" className={className}>
      {risk} risk
    </Badge>
  );
}

function formatPeriod(report: {
  period_start: string;
  period_end: string;
}) {
  return `${report.period_start} -> ${report.period_end}`;
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString();
}

function money(value: number | null | undefined) {
  if (value == null) return 'n/a';
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function number(value: number | null | undefined) {
  if (value == null) return 'n/a';
  return Math.round(value).toLocaleString();
}

function decimal(value: number | null | undefined) {
  if (value == null) return 'n/a';
  return value.toLocaleString(undefined, {
    maximumFractionDigits: 1,
  });
}

function percent(value: number | null | undefined) {
  if (value == null) return 'n/a';
  return `${(value * 100).toFixed(1)}%`;
}
